import styled, { keyframes } from 'styled-components';
import { Trilha } from '../../components/Trilha';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* O banner próprio da Nossa História: foto centralizada com parallax, véu
   escuro, selo e título aparecendo com animação, ícone do mouse embaixo. Não
   usa a Capa padrão de propósito -- a foto antiga pede o enquadramento inteiro. */
export const Banner = styled.section`
  position: relative;
  /* a mesma altura da Capa padrão das outras páginas */
  height: clamp(22rem, 30vw, 35rem);
  overflow: hidden;
  background: #0a3a1f;
`;

/* A foto cobre o banner inteiro. Ela é feita na proporção dele (3840x1120,
   ~3,43:1): até 1920px de tela não há corte de altura; no celular corta os lados. */
export const BannerBackground = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.2s ease-out;
  will-change: transform;
`;

export const BannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
`;

/* Breadcrumb automático no alto, alinhado como nas outras capas. */
export const TrilhaNoBanner = styled(Trilha)`
  position: absolute;
  top: 1.5rem;
  left: 0;
  right: 0;
  z-index: 20;
  width: 100%;
  max-width: 1200px;
  margin-inline: auto;
  padding-inline: clamp(1rem, 4vw, 2.5rem);
  box-sizing: border-box;

  @media (max-width: 720px) {
    top: 1rem;
  }
`;

export const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* selo no alto, logo abaixo do breadcrumb; título e texto embaixo, acima do
     ícone do mouse */
  justify-content: flex-start;
  position: relative;
  z-index: 10;
  height: 100%;
  box-sizing: border-box;
  text-align: center;
  padding: 4.25rem 1rem 5.25rem;
  color: #fff;

  h1 {
    font-size: clamp(1.85rem, 1.3rem + 1.6vw, 2.3rem);
    margin-bottom: 0.875rem;
    font-weight: bold;
    opacity: 0;
    animation: ${fadeInUp} 1s ease-out 0.5s forwards;
  }

  p {
    font-size: clamp(0.9375rem, 0.88rem + 0.25vw, 1.0625rem);
    max-width: 720px;
    margin: 0 auto;
    line-height: 1.6;
    opacity: 0;
    animation: ${fadeInUp} 1s ease-out 0.8s forwards;
  }
`;

/* O selo fica no alto do banner; a margem automática empurra o título para baixo. */
export const AnimatedWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: auto;
  z-index: 20;
  opacity: 0;
  animation: ${fadeInUp} 1s ease-out 0.5s forwards;
`;

/* Selo cáqui (#ACAB81), que conversa com a foto antiga, com texto e ponto no
   verde-escuro. */
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background: #acab81;
  color: #173d2b;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.6;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);

  &::before {
    content: '';
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background: #173d2b;
  }
`;

export const Container = styled.div`
  min-height: 100vh;
  background-color: #fff;
`;







export const OrigemWrapper = styled.section`
  padding: 5rem 1rem;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
`;

export const OrigemContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const OrigemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const OrigemBadge = styled.span`
  display: block;
  width: fit-content;
  color: var(--second-color);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.4;
  text-transform: uppercase;
`;

export const OrigemTitle = styled.div`
  font-size: clamp(1.75rem, 1.2rem + 1.6vw, 2.4rem);
  font-weight: 700;
  line-height: 1.2;
  color: #17251b;
  text-wrap: balance;

  /* Traço âmbar sob o título, como nos títulos de seção da Home. */
  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 0.1875rem;
    margin: 0.875rem 0 0;
    border-radius: 0.25rem;
    background: var(--sixth-color);
  }
`;

export const OrigemDescription = styled.div`
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: justify;

  p + p {
    margin-top: 1rem;
  }
`;

export const OrigemStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  padding-top: 1.5rem;

  div {
    text-align: center;
  }
`;

export const OrigemStatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #16a34a;
`;

export const OrigemStatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const OrigemImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const OrigemImagePlaceholder = styled.div`
  width: 100%;
  max-width: 360px;
  aspect-ratio: 1;
  background: #e5e7eb;
  border-radius: 1rem;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;


  svg {
  position: absolute;
  width: 3rem;
  height: 3rem;
  bottom: -1.5rem;
  right: -1.5rem;
  background: #16a34a;
  color: white;
  padding: 1rem;
  clip-path: circle();
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

`;









export const BadgeTitle = styled.span`
  display: block;
  width: fit-content;
  color: var(--second-color);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.4;
  text-transform: uppercase;
  margin: 0 auto;
`;

export const Section = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: clamp(3.5rem, 6vw, 5rem) 1rem;
  background: ${({ bg }) => bg || 'white'};
`;

export const TimelineHeader = styled.div`
  text-align: center;
  margin: 0.75rem auto 2.25rem;

  h2 {
    margin: 0 0 1rem;
    font-size: clamp(1.75rem, 1.2rem + 1.6vw, 2.4rem);
    font-weight: 700;
    line-height: 1.2;
    color: #17251b;
    text-wrap: balance;

    /* Traço âmbar sob o título, como nos títulos de seção da Home. */
    &::after {
      content: '';
      display: block;
      width: 3rem;
      height: 0.1875rem;
      margin: 0.875rem auto 0;
      border-radius: 0.25rem;
      background: var(--sixth-color);
    }
  }

  p {
    color: #4b5563;
    font-size: 0.9375rem;
    max-width: 600px;
    margin: 0 auto;
  }
`;

export const TimelineNav = styled.div`
  display: flex;
  width: fit-content;
  max-width: min(100%, 56rem);
  box-sizing: border-box;
  box-shadow: 0 2px 10px rgba(15, 78, 43, 0.08);
  gap: 1rem;
  overflow-x: auto;
  padding: 0.8rem 2rem;
  /* branco: o cinza de antes ficava apagado sobre o verde-claro da seção */
  background: #fff;
  border-radius: 9999px;
  margin: 0 auto 2rem auto;
   /* 🔒 Esconde a scrollbar no Chrome, Safari e Edge */
  &::-webkit-scrollbar {
    display: none;
  }

  /* 🔒 Esconde scrollbar no Firefox */
  scrollbar-width: none;

  scroll-snap-type: x mandatory;

  @media (max-width: 550px) {
    max-width: 100%;
    padding: 0.8rem 2rem;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
  }


  button {
    flex: 0 0 auto;
    padding: 0.7rem 1.2rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    background-color: #f1f5f2; /* verde-acinzentado claro, sobre a barra branca */
    color: #374151; /* texto cinza escuro */
    border: none;
    cursor: pointer;
    transition: all 0.4s ease;
    scroll-snap-align: center;
    
    &.active {
      background-color: #16a34a; /* verde */
      color: #ffffff;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      transform: scale(1.05);
    }
    &:not(.active):hover {
    background-color: #e3ece5;
  }
  }
`;



export const TimelineContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.6s ease both;
`;

export const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: all 0.4s ease;
  margin-bottom: 2rem;
  
  &.animate {
    animation: ${fadeInUp} 0.6s ease;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

export const CardYear = styled.span`
  display: inline-block;
  background: #16a34a;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;

  :hover{
    background-color: #586069;
  }
`;

export const CardTitle = styled.h3`
  color: #111827;
  font-size: 1.25rem;
  font-weight: bold;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;


export const CardText = styled.p`
  font-size: 1rem;
  color: #4b5563;
  text-align: justify;
`;


export const Gallery = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

export const ImgButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;

  img {
    display: block;
    width: 160px;
    height: 110px;
    object-fit: cover;
    border-radius: 0.5rem;
    transition: transform 0.3s;
  }

  &:hover img {
    transform: scale(1.05);
  }

  svg {
    position: absolute;
    bottom: 5px;
    right: 5px;
    background: white;
    border-radius: 50%;
    padding: 2px;
    color: #16a34a;
  }
`;

export const ModalImage = styled.img`
  width: 100%;
  max-width: 700px;
  border-radius: 8px;
`;


export const ValoresWrapper = styled.section`
  /* embaixo um pouco mais: o botão "voltar ao topo" do rodapé avança sobre a seção */
  padding: 5rem 1rem 6rem;
  background-color: #fff;
  text-align: center;
`;

export const ValoresBadge = styled.span`
  display: block;
  width: fit-content;
  color: var(--second-color);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.4;
  text-transform: uppercase;
  margin: 0 auto 0.5rem;
`;

export const ValoresTitle = styled.h2`
  font-size: clamp(1.75rem, 1.2rem + 1.6vw, 2.4rem);
  font-weight: 700;
  line-height: 1.2;
  color: #17251b;
  text-wrap: balance;

  /* Traço âmbar sob o título, como nos títulos de seção da Home. */
  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 0.1875rem;
    margin: 0.875rem auto 0;
    border-radius: 0.25rem;
    background: var(--sixth-color);
  }
  margin: 0 0 3rem;
`;


/* export const ValoresGrid = styled.div`
  display: grid;
  gap: 2rem;
  max-width: 1024px;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`; */
export const ValoresGrid = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1024px;
  flex-wrap: wrap;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const ValorCard = styled.div`
  width: 250px;
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-0.5rem);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

export const ValorIconWrapper = styled.div`
  width: 4rem;
  height: 4rem;
  background-color: #84eea9;
  color: #16a34a;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  transition: background-color 0.3s, color 0.3s;

  ${ValorCard}:hover & {
    background-color: #16a34a;
    color: white;
  }
`;


export const ValorTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
`;

export const ValorDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.6;
`;